import { useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Settings, LogOut, ArrowLeft, Menu, X, FileText, Headset } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
    { to: "/admin/orders", label: "Orders", icon: ShoppingBag, testid: "admin-nav-orders" },
    { to: "/admin/quotes", label: "Bulk Quotes", icon: FileText, testid: "admin-nav-quotes" },
    { to: "/admin/handoffs", label: "Chat Handoffs", icon: Headset, testid: "admin-nav-handoffs" },
    { to: "/admin/inventory", label: "Inventory", icon: Package, testid: "admin-nav-inventory" },
    { to: "/admin/products", label: "Product Editor", icon: Settings, testid: "admin-nav-products" },
];

export default function AdminLayout() {
    const { user, checked, logout } = useAuth();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    if (!checked) {
        return (
            <div className="min-h-screen flex items-center justify-center text-neutral-500">
                Loading…
            </div>
        );
    }
    if (!user || user === false) {
        return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }
    if (user.role !== "admin") {
        return (
            <div className="max-w-md mx-auto px-6 py-32 text-center" data-testid="admin-forbidden">
                <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-3">// 403 · Forbidden</div>
                <h1 className="font-display text-4xl font-extrabold uppercase tracking-tighter mb-4">
                    Admin access required
                </h1>
                <p className="text-neutral-600 text-sm leading-relaxed">
                    Your account doesn&apos;t have permission to view this area. Sign in with an admin account
                    to continue.
                </p>
                <NavLink
                    to="/"
                    className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.18em] text-[#0077b6] hover:underline"
                >
                    <ArrowLeft className="w-3 h-3" /> Back to site
                </NavLink>
            </div>
        );
    }

    const sidebar = (
        <aside className="bg-white lg:border-r lg:border-neutral-200 h-full lg:min-h-screen flex flex-col">
            <div className="px-5 lg:px-6 py-5 lg:py-6 border-b border-neutral-200 flex items-center gap-3">
                <div className="relative w-7 h-7">
                    <div className="absolute inset-0 rounded-lg border border-[#00aeef]" />
                    <div className="absolute inset-1.5 rounded-lg bg-[#f8f8f8]/30" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-extrabold uppercase tracking-[0.18em] text-sm leading-none">
                        Nexus<span className="text-[#0077b6]">.</span>Admin
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mt-1 truncate">
                        {user.email}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="lg:hidden p-2 text-neutral-500 hover:text-black"
                    aria-label="Close menu"
                    data-testid="admin-sidebar-close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
                {NAV_ITEMS.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                isActive
                                    ? "bg-neutral-100 text-neutral-900 border border-[#00aeef]/30"
                                    : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100/5 border border-transparent"
                            }`
                        }
                        data-testid={n.testid}
                    >
                        <n.icon className="w-4 h-4" /> {n.label}
                    </NavLink>
                ))}
            </nav>

            <div className="px-3 py-4 border-t border-neutral-200 space-y-1">
                <NavLink
                    to="/"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-black"
                    data-testid="admin-back-to-site"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to site
                </NavLink>
                <button
                    type="button"
                    onClick={logout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-red-400"
                    data-testid="admin-logout"
                >
                    <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
            </div>
        </aside>
    );

    return (
        <div className="min-h-screen bg-[#ffffff] text-neutral-900" data-testid="admin-layout">
            {/* Mobile top bar (only visible <lg) */}
            <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-neutral-200 flex items-center justify-between px-4 h-14">
                <div className="flex items-center gap-2">
                    <div className="relative w-6 h-6">
                        <div className="absolute inset-0 rounded-md border border-[#00aeef]" />
                        <div className="absolute inset-1 rounded-md bg-[#f8f8f8]/30" />
                    </div>
                    <div className="font-display font-extrabold uppercase tracking-[0.18em] text-xs">
                        Nexus<span className="text-[#0077b6]">.</span>Admin
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    className="p-2 border border-neutral-200 rounded-lg hover:border-[#0089c8]"
                    aria-label="Open menu"
                    data-testid="admin-sidebar-open"
                >
                    <Menu className="w-4 h-4" />
                </button>
            </div>

            {/* Mobile drawer + backdrop */}
            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-40 flex" data-testid="admin-mobile-drawer">
                    <button
                        type="button"
                        onClick={() => setMobileOpen(false)}
                        aria-label="Close menu backdrop"
                        className="absolute inset-0 bg-white/70 backdrop-blur-sm"
                    />
                    <div className="relative w-72 max-w-[85%] h-full ml-0 shadow-2xl">{sidebar}</div>
                </div>
            )}

            <div className="lg:grid lg:grid-cols-[260px_1fr]">
                <div className="hidden lg:block sticky top-0 h-screen">{sidebar}</div>

                <main className="px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
